import { useCallback, useRef, useState } from 'react';
import { Botao, Cartao } from './Interface';

type Pedido = {
  titulo: string;
  texto: string;
  confirmar: string;
  /** quando presente, o dialogo pede um texto (no lugar do prompt do navegador) */
  campo?: { rotulo: string; sugestao?: string; obrigatorio?: boolean };
  perigo?: boolean;
};

/**
 * ARMADILHA 11: o dialogo precisa estar renderizado na tela.
 * Por isso o hook devolve o elemento junto — quem usa e obrigado a
 * colocar {dialogo} no JSX, senao a promessa nunca resolveria.
 */
export function useConfirmacao() {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [texto, setTexto] = useState('');
  const resolver = useRef<((r: { ok: boolean; texto: string }) => void) | null>(null);

  const confirmar = useCallback((p: Pedido) => {
    setPedido(p);
    setTexto('');
    return new Promise<boolean>((resolve) => {
      resolver.current = ({ ok }) => resolve(ok);
    });
  }, []);

  /** Igual ao confirmar, mas devolve o que a pessoa escreveu. */
  const perguntar = useCallback((p: Pedido) => {
    setPedido(p);
    setTexto('');
    return new Promise<{ ok: boolean; texto: string }>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const responder = (ok: boolean) => {
    const escrito = texto;
    setPedido(null);
    setTexto('');
    resolver.current?.({ ok, texto: escrito });
    resolver.current = null;
  };

  const dialogo = pedido ? (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overscroll-contain bg-tinta-900/40 p-4"
      onClick={() => responder(false)}
    >
      <div className="my-auto w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <Cartao>
          <div className="flex items-start gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg ${
                pedido.perigo ? 'bg-red-50 text-red-600' : 'bg-tinta-900 text-white'
              }`}
            >
              {pedido.perigo ? '!' : '?'}
            </span>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-tinta-900">{pedido.titulo}</h3>
              <p className="mt-1 text-sm text-tinta-500">{pedido.texto}</p>
            </div>
          </div>

          {pedido.campo && (
            <label className="mt-4 block">
              <span className="mb-1.5 block text-sm font-medium text-tinta-700">
                {pedido.campo.rotulo}
              </span>
              <input
                autoFocus
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (!pedido.campo?.obrigatorio || texto.trim())) responder(true);
                  if (e.key === 'Escape') responder(false);
                }}
                placeholder={pedido.campo.sugestao}
                className="w-full rounded-xl border border-tinta-200 px-3.5 py-2.5"
              />
            </label>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Botao variante="secundario" onClick={() => responder(false)}>
              Cancelar
            </Botao>
            <Botao
              disabled={Boolean(pedido.campo?.obrigatorio) && !texto.trim()}
              className={pedido.perigo ? 'bg-red-600 hover:bg-red-700' : ''}
              onClick={() => responder(true)}
            >
              {pedido.confirmar}
            </Botao>
          </div>
        </Cartao>
      </div>
    </div>
  ) : null;

  return { confirmar, perguntar, dialogo };
}
